import { useState } from "react";

import TextAreaWithCounter from "@hl/base-components/lib/inputs/TextAreaWithCounter";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import { STATUS_COLOR } from "@hl/base-components/lib/theme/colors";
import {
  FEATURE_FLAGS,
  useFeatureFlags,
} from "@hl/shared-features/lib/features/auth/hooks";
import CryptoCurrencyInput from "@hl/shared-features/lib/features/input/CryptoCurrencyInput";
import { Button, NumberInput, Stack, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { ethers } from "ethers";
import { capitalize } from "lodash";

import { PriceType, TokenDistributionType } from "apollo/graphql.generated";
import { GateSelectOnCreate } from "components/GateSelect";
import { CurrencyInput } from "components/sale/CurrencyInput";
import { PayoutAddressInput } from "components/sale/PayoutAddressInput";
import { MintDetails, PriceTypeInput } from "components/sale/PriceTypeInput";
import { SaleDatesInput } from "components/sale/SaleDatesInput";
import { SaleLimitMintsInput } from "components/sale/SaleLimitMintsInput";
import SaleTypeInput from "components/sale/SaleTypeInput";
import DutchAuctionsCurrencyInput from "~/currency/DutchAuctionsCurrencyInput";
import DutchAuctionsPriceDropIntervalInput from "~/currency/DutchAuctionsPriceDropIntervalInput";
import { REASONABLE_SALE_PRICE_LIMIT } from "~/features/collections/forms/CreateCollectionMultistepForm/CollectionPriceForm";
import { useGatesQuery } from "~/features/gates/queries/gates.graphql.generated";
import {
  MintVector,
  saleTitleMap,
} from "~/features/tokens/sections/ManageToken/MintVector/MultipleMintVector";
import useManageSale from "~/features/tokens/sections/ManageToken/MintVector/useManageSale";
import useSaleForm, {
  SaleForm,
  SaleType,
} from "~/features/tokens/sections/ManageToken/MintVector/useSaleForm";
import SaleStatusBox from "~/features/tokens/sections/ManageToken/SaleStatusBox";
import { GetManageTokenCollectionQuery } from "~/features/tokens/sections/ManageToken/queries.graphql.generated";
import { getSaleStatus } from "~/features/tokens/sections/ManageToken/types";

type ManageSaleData = {
  onClose: () => void;
  distributionType: TokenDistributionType;
  mintVector?: MintVector | null;
  collection: GetManageTokenCollectionQuery["getCollection"];
  isFixedPriceCollection: boolean;
  enableGatelessPrivateSales: boolean;
  disableNewPublicSale?: boolean;
  firstSale?: boolean;
};

export const ManageSale = ({
  mintVector,
  distributionType,
  collection,
  onClose,
  isFixedPriceCollection,
  enableGatelessPrivateSales,
  disableNewPublicSale = false,
  firstSale,
}: ManageSaleData) => {
  const { initialValues, validate } = useSaleForm({
    mintVector,
    isFixedPriceCollection,
    collection,
    enableGatelessPrivateSales,
  });
  const {
    buttonDisabled,
    buttonLabel,
    error,
    loading: loadingSubmit,
    handleSubmit,
    updatePauseForPublicDutchAuctionSale,
  } = useManageSale(collection, onClose, mintVector);

  const form = useForm<SaleForm>({
    initialValues,
    validate: (values) => validate(values, collection),
  });

  const { data: gatesData, loading: gatesLoading } = useGatesQuery();

  const [loadingCurrencyInput, setLoadingCurrencyInput] = useState(false);

  const enableCreatorRewards = useFeatureFlags(
    FEATURE_FLAGS.ENABLE_CREATOR_REWARDS
  );

  const isPublic = form.values.saleType === SaleType.PUBLIC;
  const isDutchAuction =
    form.values.mintDetails.priceType === PriceType.DutchAuction;
  const isPublicDutchAuction = isPublic && isDutchAuction;
  const saleStatus = getSaleStatus(mintVector, collection, {
    ignorePaused: !isPublicDutchAuction,
  }); // Ignore the paused state so the UI shows the correct state after unpausing
  const saleTitle = saleTitleMap[saleStatus];
  const isEdition = !!mintVector;
  const isFixedPrice = form.values.mintDetails.priceType === PriceType.Fixed;
  const isFreePrice =
    form.values.mintDetails.isFreePrice && !!enableCreatorRewards;
  const isOnchainDutchAuctionWithMintedTokens =
    isPublicDutchAuction && !!mintVector?.stats?.sold;

  const [ensLoading, setEnsLoading] = useState(false);

  const handleOnChangePriceType = (value: MintDetails) => {
    form.setFieldValue("mintDetails", value);
    if (value.isFreePrice) {
      form.setFieldValue("price", 0);
    }
    form.setFieldValue("currency", ethers.constants.AddressZero);
  };

  return (
    <form onSubmit={form.onSubmit(async (values) => handleSubmit(values))}>
      <Stack spacing={24}>
        {isEdition && (
          <SaleStatusBox
            withBorder
            saleConfig={saleTitle}
            showEditButton={isPublicDutchAuction}
            editButton={
              buttonLabel || (form.values.isPaused ? "Unpause" : "Pause")
            }
            onEditSaleClick={() =>
              updatePauseForPublicDutchAuctionSale(!form.values.isPaused)
            }
            error={error}
            startDate={form.values.saleDateFrom}
            isSalePaused={form.values.isPaused}
            onTogglePauseSaleClick={
              isPublicDutchAuction
                ? undefined
                : () => {
                    form.setFieldValue("isPaused", !form.values.isPaused);
                  }
            }
            loading={loadingSubmit || buttonDisabled}
          />
        )}
        <PriceTypeInput
          collectionType={collection.collectionType}
          value={form.values.mintDetails}
          onChange={handleOnChangePriceType}
          disableEnglishAuction
        />
        {!isEdition && !enableGatelessPrivateSales && (
          <SaleTypeInput
            disablePublicSale={disableNewPublicSale}
            isDutchAuction={isDutchAuction}
            value={form.values.saleType}
            onChange={(saleType) => {
              form.setFieldValue("saleType", saleType);
              form.setFieldValue(
                "saleName",
                saleType === SaleType.PUBLIC ? "Public sale" : ""
              );
            }}
          />
        )}
        {isFixedPrice && (
          <CurrencyInput
            label={isFreePrice ? "Mint fee currency" : "Currency"}
            description={
              isFreePrice
                ? "The currency collectors will pay and you will receive in mint rewards"
                : "Select the currency you want collectors to pay"
            }
            value={form.values.currency}
            onChange={(value) => form.setFieldValue("currency", value)}
            onLoading={(loading) => setLoadingCurrencyInput(loading)}
            chainId={collection.chainId}
            error={form.errors.currency}
          />
        )}
        {!isPublic && (
          <GateSelectOnCreate
            description="Grant exclusive, early, or discounted access for specific collectors"
            gates={gatesData?.gates ?? []}
            value={form.values.gateId ?? ""}
            onChange={(newGateId: string) =>
              form.setFieldValue("gateId", newGateId)
            }
            error={form.errors.gateId}
            isGatingMint
            isGatingAuction={false}
            isDirectMint={false}
          />
        )}
        <TextInput
          label="Sale name"
          placeholder={`${capitalize(form.values.saleType)} sale`}
          disabled={isPublic}
          {...form.getInputProps("saleName")}
        />
        {!isPublic && (
          <TextAreaWithCounter
            placeholder="Leave an optional note for your collectors"
            label="Note to collectors"
            minRows={3}
            {...form.getInputProps("collectorMessage")}
            autosize
            maxLength={150}
            characterCounter
          />
        )}
        {isFixedPrice && !isFreePrice && (
          <CryptoCurrencyInput
            label="Price"
            placeholder="0.1"
            missingChainIdPlaceholder="Select a blockchain before entering price"
            chainId={collection.chainId}
            max={REASONABLE_SALE_PRICE_LIMIT}
            currencyAddress={form.values.currency}
            {...form.getInputProps("price")}
          />
        )}
        {isDutchAuction && !isOnchainDutchAuctionWithMintedTokens && (
          <Stack spacing={24}>
            <DutchAuctionsCurrencyInput
              value={form.values.scheduledPrices}
              chainId={collection.chainId}
              maxPrices={15}
              error={form.getInputProps("scheduledPrices").error}
              onChange={form.getInputProps("scheduledPrices").onChange}
            />
            <DutchAuctionsPriceDropIntervalInput
              value={form.values.priceDropInterval}
              error={form.getInputProps("priceDropInterval").error}
              onChange={form.getInputProps("priceDropInterval").onChange}
            />
          </Stack>
        )}

        {!isOnchainDutchAuctionWithMintedTokens && (
          <NumberInput
            label="Sale size"
            placeholder="100"
            //min={1}
            //max={collection.size ?? undefined}
            {...form.getInputProps("saleSize")}
          />
        )}
        <SaleDatesInput
          distributionType={distributionType}
          dateToProps={form.getInputProps("saleDateTo")}
          dateFromProps={form.getInputProps("saleDateFrom")}
          disableStartDate={isOnchainDutchAuctionWithMintedTokens}
        />
        <SaleLimitMintsInput
          enableLimitInputProps={form.getInputProps(
            "shouldLimitNumberOfMintPerWallet",
            {
              type: "checkbox",
            }
          )}
          limitInputProps={form.getInputProps("limitPerWallet")}
          distributionType={distributionType}
        />
        <PayoutAddressInput
          onChangeLoading={(loading) => setEnsLoading(loading)}
          onChange={(customRecipient) => {
            form.setFieldValue(
              "paymentRecipient",
              customRecipient.address || ""
            );
          }}
          onError={(error) => form.setFieldError("paymentRecipientEOA", error)}
          distributionType={distributionType}
          addressInputProps={{
            disabled: !firstSale,
            ...form.getInputProps("paymentRecipientEOA"),
          }}
        />
      </Stack>

      <ModalSection>
        <Button
          type="submit"
          size="xl"
          fullWidth
          loading={
            gatesLoading ||
            loadingSubmit ||
            buttonDisabled ||
            ensLoading ||
            loadingCurrencyInput
          }
        >
          {buttonLabel || (mintVector ? "Save changes" : "Create sale")}
        </Button>
        {isPublic && (
          <Text size="xs" align="center" mt={8}>
            This action requires a transaction
          </Text>
        )}
        {error && (
          <Text align="center" mt={8} size="xs" color={STATUS_COLOR.ERROR}>
            {error}
          </Text>
        )}
      </ModalSection>
    </form>
  );
};
